/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommandResource, tapOnCommandSuccessfullyDone } from '@alfa-client/command-shared';
import { NavigationService } from '@alfa-client/navigation-shared';
import { VorgangHeaderLinkRel, VorgangService, VorgangWithEingangResource } from '@alfa-client/vorgang-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { ApiRootLinkRel, ApiRootResource, ApiRootService } from '@common/api-root-shared';
import {
  ApiError,
  createEmptyStateResource,
  createErrorStateResource,
  createStateResource,
  doIfLoadingRequired,
  isApiError,
  isLoaded,
  isLoadingRequired,
  isNotNull,
  mapToResource,
  MessageCode,
  StateResource,
} from '@common/tech-shared';
import { getEmbeddedResource, getUrl, hasLink, Resource, ResourceUri } from '@ngxp/rest';
import { DropdownMenuComponent } from '@ods/system';
import { isNil } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, first, map, startWith, switchMap, tap } from 'rxjs/operators';
import { UserProfileResourceSearchService } from './user-profile-resource-search.service';
import { UserProfileListLinkRel } from './user-profile.linkrel';
import { UserProfileListResource, UserProfileResource } from './user-profile.model';
import { UserProfileRepository } from './user-profile.repository';

@Injectable()
export class UserProfileService {
  private static readonly MEIN_PROFIL_PATH: string = '/mein-profil';

  private readonly repository = inject(UserProfileRepository);
  private readonly apiRootService = inject(ApiRootService);
  private readonly navigationService = inject(NavigationService);
  private readonly vorgangService = inject(VorgangService);
  private readonly resourceSearchService = inject(UserProfileResourceSearchService);

  _dropDownComp: DropdownMenuComponent;
  _userProfiles: { [key: string]: BehaviorSubject<StateResource<UserProfileResource>> } = {};
  private readonly currentUser: BehaviorSubject<StateResource<UserProfileResource>> = new BehaviorSubject(
    createEmptyStateResource<UserProfileResource>(),
  );

  private navigationSubscription: Subscription;

  constructor() {
    this.listenOnNavigation();
  }

  private listenOnNavigation(): void {
    if (!isNil(this.navigationSubscription)) this.navigationSubscription.unsubscribe();
    this.navigationSubscription = this.navigationService.urlChanged().subscribe((params) => this.onNavigation(params));
  }

  onNavigation(params: Params): void {
    if (NavigationService.isVorgangListPage(params)) {
      this._userProfiles = {};
    } else if (NavigationService.isVorgangDetailPage(params, VorgangService.VORGANG_WITH_EINGANG_URL)) {
      this._userProfiles = {};
    }
  }

  public getAssignedUserProfile(assignedTo: Resource, linkRel: string): any {
    if (!hasLink(assignedTo, linkRel)) return of(createEmptyStateResource<UserProfileResource>());

    const uri: ResourceUri = getUrl(assignedTo, linkRel);

    this.createIfNotExist(uri);
    this._loadUserPofileIfRequired(assignedTo, linkRel);
    return this._userProfiles[uri].asObservable();
  }

  _loadUserPofileIfRequired(assignedTo: Resource, linkRel: string): void {
    doIfLoadingRequired(this._userProfiles[getUrl(assignedTo, linkRel)].value, () => {
      this.loadUserProfile(assignedTo, linkRel);
    });
  }

  public getAssignedUserProfileFromVorgang(): any {
    return this.vorgangService.selectVorgangWithEingang().pipe(
      filter(isLoaded),
      mapToResource<VorgangWithEingangResource>(),
      switchMap((vorgang: VorgangWithEingangResource) => {
        return this.getAssignedUserProfile(vorgang, VorgangHeaderLinkRel.ASSIGNED_TO);
      }),
    );
  }

  public getCurrentUser(): Observable<StateResource<UserProfileResource>> {
    return combineLatest([this.getCurrentUserFromState(), this.apiRootService.getApiRoot()]).pipe(
      filter(([, apiRoot]) => isNotNull(apiRoot.resource)),
      tap(([currentUser, apiRoot]) => this.handleGetCurrentUser(currentUser, apiRoot.resource)),
      map(([currentUser]) => currentUser),
      startWith(createEmptyStateResource<UserProfileResource>(true)),
    );
  }

  getCurrentUserFromState(): Observable<StateResource<UserProfileResource>> {
    return this.currentUser.asObservable();
  }

  public getMeinProfilUrl(): Observable<string | undefined> {
    return this.apiRootService
      .getApiRoot()
      .pipe(
        map((stateResource: StateResource<ApiRootResource>) =>
          this.buildMeinProfilUrl(stateResource.resource?.administrationUrl),
        ),
      );
  }

  private buildMeinProfilUrl(administrationUrl: string | undefined): string | undefined {
    if (!administrationUrl) {
      return undefined;
    }
    const baseUrl: string = administrationUrl.endsWith('/') ? administrationUrl.slice(0, -1) : administrationUrl;
    return baseUrl + UserProfileService.MEIN_PROFIL_PATH;
  }

  handleGetCurrentUser(currentUser: StateResource<UserProfileResource>, apiRoot: ApiRootResource): void {
    if (hasLink(apiRoot, ApiRootLinkRel.CURRENT_USER) && isLoadingRequired(currentUser)) {
      this.loadCurrentUser(apiRoot);
    }
  }

  loadCurrentUser(apiRoot: ApiRootResource): void {
    this.setCurrentUserLoading();
    this.repository
      .getCurrentUser(apiRoot)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse)),
        first(),
      )
      .subscribe((currentUser: ApiError | UserProfileResource) =>
        this.setCurrentUser(this.createStateResourceForUpdate(currentUser)),
      );
  }

  setCurrentUserLoading(): void {
    this.currentUser.next(createEmptyStateResource<UserProfileResource>(true));
  }

  setCurrentUser(userProfile: StateResource<UserProfileResource>): void {
    this.currentUser.next(userProfile);
  }

  createIfNotExist(uri: ResourceUri): void {
    if (isNil(this._userProfiles[uri]))
      this._userProfiles[uri] = new BehaviorSubject(createEmptyStateResource<UserProfileResource>());
  }

  loadUserProfile(vorgang: Resource, linkRel: string): void {
    this.setUserProfileLoading(getUrl(vorgang, linkRel));
    this.repository
      .getUserProfile(vorgang, linkRel)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse)),
        first(),
      )
      .subscribe((response: ApiError | UserProfileResource) =>
        this.updateUserProfile(vorgang, linkRel, this.createStateResourceForUpdate(response)),
      );
  }

  private setUserProfileLoading(uri: ResourceUri): void {
    this._userProfiles[uri].next(createEmptyStateResource<UserProfileResource>(true));
  }

  handleError(errorResponse: HttpErrorResponse): Observable<ApiError> {
    return of(this.handleErrorByStatus(errorResponse));
  }

  handleErrorByStatus(error: HttpErrorResponse): ApiError {
    if (this.isNetworkError(error.status)) {
      return this.buildServiceUnavailableApiError();
    }
    return error.error;
  }

  private isNetworkError(status: number): boolean {
    return status == 0;
  }

  private buildServiceUnavailableApiError(): ApiError {
    return <ApiError>{ issues: [{ messageCode: MessageCode.SERVICE_UNAVAILABLE }] };
  }

  createStateResourceForUpdate(response: ApiError | UserProfileResource): StateResource<UserProfileResource> {
    if (isApiError(response)) {
      return createErrorStateResource(<ApiError>response);
    } else {
      return createStateResource(<UserProfileResource>response);
    }
  }

  updateUserProfile(vorgang: Resource, linkRel: string, userProfile: StateResource<UserProfileResource>): void {
    //TOCHECK Potentielle Fehlerstelle: "cannot read 'next' of null/undefined"
    this._userProfiles[getUrl(vorgang, linkRel)].next(userProfile);
  }

  public getSearchedUserProfiles(username: string): Observable<StateResource<UserProfileResource>[]> {
    return this.resourceSearchService.getResultList().pipe(
      filter(isLoaded),
      map((listStateResource: StateResource<UserProfileListResource>) =>
        this.filterProfiles(username, listStateResource.resource),
      ),
    );
  }

  private filterProfiles(username: string, listResource: UserProfileListResource): StateResource<UserProfileResource>[] {
    const profiles: UserProfileResource[] = getEmbeddedResource(listResource, UserProfileListLinkRel.USER_PROFILE_LIST) || [];

    return profiles
      .filter((profile) => profile.username !== username)
      .map((profile: UserProfileResource) => createStateResource(profile));
  }

  public search(searchBy: string): void {
    this.resourceSearchService.search(searchBy);
  }

  public assign(userProfile: UserProfileResource): Observable<StateResource<CommandResource>> {
    return this.closeDropdownOnSuccess(this.vorgangService.assignUser(getUrl(userProfile)));
  }

  public unassign(): Observable<StateResource<CommandResource>> {
    return this.closeDropdownOnSuccess(this.vorgangService.unassignUser());
  }

  private closeDropdownOnSuccess<T>(
    command$: Observable<StateResource<CommandResource>>,
  ): Observable<StateResource<CommandResource>> {
    return command$.pipe(tapOnCommandSuccessfullyDone(() => this.closeDropdown()));
  }

  registerDropdown(dropdownComp: DropdownMenuComponent): void {
    this._dropDownComp = dropdownComp;
  }

  openDropdown(): void {
    this._dropDownComp.open();
  }

  closeDropdown(): void {
    this._dropDownComp.close();
  }
}
